/**
 * Learning Path Store
 *
 * State management for learning paths.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { learningPathApi } from '../api/learningPath.api'
import type { LearningPath, UserPathProgress } from '../types/learningPath.types'

interface LearningPathState {
  paths: LearningPath[]
  userPaths: UserPathProgress[]
  selectedPath: LearningPath | null
  isLoading: boolean
  error: string | null

  fetchPaths: () => Promise<void>
  fetchUserPaths: (userId: string) => Promise<void>
  fetchPathById: (id: string) => Promise<void>
  enrollInPath: (pathId: string, userId: string) => Promise<boolean>
  abandonPath: (pathId: string, userId: string) => Promise<boolean>
}

export const useLearningPathStore = createWithEqualityFn<LearningPathState>((set) => ({
  paths: [],
  userPaths: [],
  selectedPath: null,
  isLoading: false,
  error: null,

  fetchPaths: async () => {
    set({ isLoading: true, error: null })
    const res = await learningPathApi.getPaths()
    if (res.success) {
      set({ paths: res.data, isLoading: false })
    } else {
      set({ error: res.error ?? 'Failed to fetch paths', isLoading: false })
    }
  },

  fetchUserPaths: async (userId: string) => {
    const res = await learningPathApi.getUserPaths(userId)
    if (res.success) {
      set({ userPaths: res.data })
    }
  },

  fetchPathById: async (id: string) => {
    set({ isLoading: true, error: null })
    const res = await learningPathApi.getPathById(id)
    if (res.success) {
      set({ selectedPath: res.data, isLoading: false })
    } else {
      set({ error: res.error ?? 'Path not found', isLoading: false })
    }
  },

  enrollInPath: async (pathId: string, userId: string) => {
    const res = await learningPathApi.enrollInPath(pathId, userId)
    if (res.success) {
      set(state => ({ userPaths: [...state.userPaths, res.data] }))
      return true
    }
    return false
  },

  abandonPath: async (pathId: string, userId: string) => {
    const res = await learningPathApi.abandonPath(pathId, userId)
    if (res.success) {
      set(state => ({
        userPaths: state.userPaths.map(p =>
          p.pathId === pathId && p.userId === userId ? res.data : p
        ),
      }))
      return true
    }
    return false
  },
}))

// Selectors
export const selectPaths = (s: LearningPathState) => s.paths
export const selectUserPaths = (s: LearningPathState) => s.userPaths
export const selectSelectedPath = (s: LearningPathState) => s.selectedPath
